import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Row, Form, Input, Label, FormFeedback } from 'reactstrap';
import ParticlesAuth from '../ParticlesAuth';
import logoLight from "../../../assets/images/logofinal.png";
import { useLocation } from "react-router-dom";
import api from "../../../api";

//formik
import { useFormik } from 'formik';
import * as Yup from 'yup';

const BasicPasswCreate = () => {

    document.title = "Create New Password | SSW Technologies";


    const [passwordShow, setPasswordShow] = useState(false);
    const [confrimPasswordShow, setConfrimPasswordShow] = useState(false);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    // Round 11: the same page serves RESET links (/auth-pass-change-basic?token=)
    // and go-live SETUP links (/set-password?token=). The purpose decides the
    // endpoint; a link of one purpose can never be used for the other.
    const isSetup = location.pathname.toLowerCase().startsWith("/set-password") ||
                    (queryParams.get("purpose") || "").toLowerCase() === "setup";
    const purpose = isSetup ? "setup" : "reset";
    const [linkState, setLinkState] = useState("checking");   // checking | valid | invalid
    useEffect(() => {
        let alive = true;
        if (!token) { setLinkState("invalid"); return () => {}; }
        api.get("Auth/link-status", { params: { token, purpose } })
            .then((r) => { if (alive) setLinkState(r?.data?.valid ? "valid" : "invalid"); })
            .catch(() => { if (alive) setLinkState("valid"); });   // never block the form on a status hiccup
        return () => { alive = false; };
      
    }, [token, purpose]);

    // Access-Management hardening (Round 1): the password policy is defined
    // ONCE on the server (GET /api/Auth/password-policy) and enforced there on
    // every password-setting flow. The page only displays it and pre-checks
    // the minimum length; composition rules (upper/lower/digit) are gone —
    // length and non-common values are what matter.
    const [policy, setPolicy] = useState({ minLength: 12, maxLength: 128, rules: [] });
    const [serverError, setServerError] = useState("");
    const [done, setDone] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get("Auth/password-policy")
            .then((r) => { if (alive && r?.data?.minLength) setPolicy(r.data); })
            .catch(() => { /* keep defaults */ });
        return () => { alive = false; };
    }, []);

    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            password: "",
            confrim_password: "",
        },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(policy.minLength, `Password must be at least ${policy.minLength} characters`)
                .max(policy.maxLength, `Password must be at most ${policy.maxLength} characters`)
                .required("This field is required"),
            confrim_password: Yup.string()
                .when("password", {
                    is: (val) => (val && val.length > 0 ? true : false),
                    then: Yup.string().oneOf(
                        [Yup.ref("password")],
                        "Both password need to be the same"
                    ),
                })
                .required("Confirm Password Required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            setServerError("");
            try {
                const response = await api.post(isSetup ? "Auth/setup-password" : "Auth/reset-password", {
                    token: token,
                    newPassword: values.password
                });

                if (response.status === 200) {
                    resetForm();
                    setDone(true);
                    setTimeout(() => { window.location.href = "/login"; }, 1500);
                }
            } catch (error) {
                // The server returns { error, errors[] } for policy violations and a
                // generic message for an invalid / expired link. Show it inline.
                const data = error?.response?.data;
                const msg = (data && (data.error || data.message)) ||
                    (typeof data === "string" ? data : "") ||
                    "We could not reset the password. The link may be invalid or expired — please request a new one.";
                setServerError(msg);
            }
        }
    });
    return (
        <ParticlesAuth>
            <div className="auth-page-content">
                <Container>
                    <Row>
                        <Col lg={12}>
                            <div className="text-center mt-sm-5 mb-4 text-white-50">
                                <div>
                                    <Link to="/" className="d-inline-block auth-logo">
                                        <img src={logoLight} alt="" height="120" />
                                    </Link>
                                </div>
                                <p className="mt-3 fs-15 fw-medium" style={{ fontSize: "25px", color: "#070161" }}>SS White Technologies Inc.</p>
                            </div>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card className="mt-4">

                                <CardBody className="p-4">
                                    <div className="text-center mt-2">
                                        <h5 className="text-primary">{isSetup ? "Set up your password" : "Create new password"}</h5>
                                        <p className="text-muted">{isSetup
                                            ? "Welcome to the SSW Dashboard. Choose the password you will sign in with."
                                            : "Choose a new password for your SSW Dashboard account."}</p>
                                    </div>

                                    {!token && (
                                        <div className="alert alert-warning mx-2" role="alert">
                                            This page must be opened from the link in your {isSetup ? "password setup" : "password reset"} e-mail.
                                        </div>
                                    )}
                                    {token && linkState === "invalid" && !done && (
                                        <div className="alert alert-warning mx-2" role="alert">
                                            {isSetup
                                                ? "This setup link is no longer valid (used or older than 72 hours). Ask the IT team to send a new one."
                                                : "This reset link is no longer valid (used or older than 30 minutes)."}
                                            {!isSetup && <> <Link to="/forgot-password">Request a new link</Link>.</>}
                                        </div>
                                    )}
                                    {serverError && (
                                        <div className="alert alert-danger mx-2" role="alert">{serverError}</div>
                                    )}
                                    {done && (
                                        <div className="alert alert-success mx-2" role="alert">
                                            Password updated. Taking you to the login page…
                                        </div>
                                    )}

                                    <div className="p-2">
                                        <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="password-input">Password</Label>
                                                <div className="position-relative auth-pass-inputgroup">
                                                    <Input
                                                        type={passwordShow ? "text" : "password"}
                                                        className="form-control pe-5 password-input"
                                                        placeholder="Enter password"
                                                        id="password-input"
                                                        name="password"
                                                        value={validation.values.password}
                                                        onBlur={validation.handleBlur}
                                                        onChange={validation.handleChange}
                                                        invalid={validation.errors.password && validation.touched.password ? true : false}
                                                    />
                                                    {validation.errors.password && validation.touched.password ? (
                                                        <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                                                    ) : null}
                                                    <Button color="link" onClick={() => setPasswordShow(!passwordShow)} className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon" type="button"
                                                        id="password-addon"><i className="ri-eye-fill align-middle"></i></Button>
                                                </div>
                                                <div id="passwordInput" className="form-text">
                                                    Must be at least {policy.minLength} characters. Any characters are allowed — a short phrase works well.
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="confirm-password-input">Confirm Password</Label>
                                                <div className="position-relative auth-pass-inputgroup mb-3">
                                                    <Input
                                                        type={confrimPasswordShow ? "text" : "password"}
                                                        className="form-control pe-5 password-input"
                                                        placeholder="Confirm password"
                                                        id="confirm-password-input"
                                                        name="confrim_password"
                                                        value={validation.values.confrim_password}
                                                        onBlur={validation.handleBlur}
                                                        onChange={validation.handleChange}
                                                        invalid={validation.errors.confrim_password && validation.touched.confrim_password ? true : false}
                                                    />
                                                    {validation.errors.confrim_password && validation.touched.confrim_password ? (
                                                        <FormFeedback type="invalid">{validation.errors.confrim_password}</FormFeedback>
                                                    ) : null}
                                                    <Button color="link" onClick={() => setConfrimPasswordShow(!confrimPasswordShow)} className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon" type="button">
                                                        <i className="ri-eye-fill align-middle"></i></Button>
                                                </div>
                                            </div>

                                            <div id="password-contain" className="p-3 bg-light mb-2 rounded">
                                                <h5 className="fs-13">Password must contain:</h5>
                                                <p id="pass-length" className="invalid fs-12 mb-2">Minimum <b>8 characters</b></p>
                                                <p id="pass-lower" className="invalid fs-12 mb-2">At <b>lowercase</b> letter (a-z)</p>
                                                <p id="pass-upper" className="invalid fs-12 mb-2">At least <b>uppercase</b> letter (A-Z)</p>
                                                <p id="pass-number" className="invalid fs-12 mb-0">A least <b>number</b> (0-9)</p>
                                            </div>

                                            <div className="form-check">
                                                <Input className="form-check-input" type="checkbox" value="" id="auth-remember-check" />
                                                <Label className="form-check-label" htmlFor="auth-remember-check">Remember me</Label>
                                            </div>

                                            <div className="mt-4">
                                                <Button color="success" className="w-100" type="submit">Reset Password</Button>
                                            </div>
                                        </Form>
                                    </div>
                                </CardBody>
                            </Card>
                            <div className="mt-4 text-center">
                                <p className="mb-0">Wait, I remember my password... <Link to="/auth-signin-basic" className="fw-semibold text-primary text-decoration-underline"> Click here </Link> </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ParticlesAuth>
    );
};

export default BasicPasswCreate;